import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Box, Menu, MenuItem, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Kept in the DOM so its width can still be measured, but not shown or focusable.
const hiddenStyle = {
  position: "absolute",
  visibility: "hidden",
  pointerEvents: "none",
};

const tabTextSx = {
  mb: 0,
  fontSize: "16px",
  fontWeight: 400,
  lineHeight: "24px",
  whiteSpace: "nowrap",
  color: "inherit",
};

const getOuterWidth = (element) => {
  if (!element) {
    return 0;
  }

  const marginRight = Number.parseFloat(
    globalThis.getComputedStyle(element).marginRight
  );

  return element.offsetWidth + (Number.isNaN(marginRight) ? 0 : marginRight);
};

/**
 * The global nav tabs. When they don't fit the available width, the tabs that don't fit move
 * into a "More" dropdown at the end of the row, so they never run into the search and user menu.
 */
const GlobalNavTabs = ({ isActive, links, onNavigate, styles, t }) => {
  const containerRef = useRef(null);
  const itemRefs = useRef([]);
  const moreRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(links.length);
  const [anchorElement, setAnchorElement] = useState(null);

  const measure = useCallback(() => {
    const available = containerRef.current?.clientWidth ?? 0;
    const widths = links.map((_, index) =>
      getOuterWidth(itemRefs.current[index])
    );
    const totalWidth = widths.reduce((sum, width) => sum + width, 0);

    // No layout (e.g. before first paint) or everything fits: show every tab.
    if (!available || totalWidth <= available) {
      setVisibleCount(links.length);
      return;
    }

    let usedWidth = getOuterWidth(moreRef.current);
    let count = 0;
    while (count < links.length && usedWidth + widths[count] <= available) {
      usedWidth += widths[count];
      count += 1;
    }
    setVisibleCount(count);
  }, [links]);

  useLayoutEffect(() => {
    measure();

    // Brand web fonts change the tab widths once they load.
    globalThis.document?.fonts?.ready?.then(measure).catch(() => {});

    if (typeof ResizeObserver === "undefined") {
      globalThis.addEventListener("resize", measure);
      return () => globalThis.removeEventListener("resize", measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [measure]);

  const overflowLinks = links.slice(visibleCount);
  const hasOverflow = overflowLinks.length > 0;
  const isMoreActive = overflowLinks.some((link) => isActive(link.url));

  const closeMenu = () => setAnchorElement(null);

  return (
    <Box className={styles.navItems} ref={containerRef}>
      {links.map((link, index) => {
        const isHidden = index >= visibleCount;

        return (
          <Link
            key={`${link.label}-${link.url}`}
            innerRef={(element) => {
              itemRefs.current[index] = element;
            }}
            className={isActive(link.url) ? styles.active : undefined}
            onClick={onNavigate}
            to={{ pathname: link.url }}
            style={isHidden ? hiddenStyle : undefined}
            tabIndex={isHidden ? -1 : undefined}
            aria-hidden={isHidden || undefined}
          >
            <Typography
              variant="body1"
              paragraph
              sx={{
                ...tabTextSx,
                color: "text.primary",
                "&:hover": { color: "primary.main" },
              }}
            >
              {t(link.label)}
            </Typography>
          </Link>
        );
      })}

      <button
        type="button"
        ref={moreRef}
        className={`${styles.navMore} ${
          isMoreActive || anchorElement ? styles.navMoreActive : ""
        }`}
        style={hasOverflow ? undefined : hiddenStyle}
        tabIndex={hasOverflow ? undefined : -1}
        aria-hidden={!hasOverflow || undefined}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchorElement)}
        aria-controls={anchorElement ? "global-nav-more-menu" : undefined}
        onClick={(event) => setAnchorElement(event.currentTarget)}
        data-testid="global-nav-more"
      >
        <Typography component="span" sx={tabTextSx}>
          {t("GlobalNavBar.more")}
        </Typography>
        <ExpandMoreIcon fontSize="small" aria-hidden="true" />
      </button>

      <Menu
        id="global-nav-more-menu"
        anchorEl={anchorElement}
        open={Boolean(anchorElement)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{ "data-testid": "global-nav-more-menu" }}
      >
        {overflowLinks.map((link) => (
          <MenuItem
            key={`${link.label}-${link.url}`}
            component={Link}
            to={{ pathname: link.url }}
            selected={isActive(link.url)}
            onClick={() => {
              onNavigate();
              closeMenu();
            }}
          >
            {t(link.label)}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

GlobalNavTabs.propTypes = {
  isActive: PropTypes.func.isRequired,
  links: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    })
  ).isRequired,
  onNavigate: PropTypes.func.isRequired,
  styles: PropTypes.objectOf(PropTypes.string).isRequired,
  t: PropTypes.func.isRequired,
};

export default GlobalNavTabs;
